/**
 * LeadCallHistoryDialog — R48 · quick call log popup from the leads list.
 *
 * A compact dialog that shows past call_logs for a lead + a "Call now"
 * button (uses existing CallButton) + a "Log a manual call" button.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import CallButton from "@/components/CallButton";
import { Phone, PhoneOff, PhoneMissed, PhoneCall, Clock, PlusCircle } from "lucide-react";

function fmtDur(s) {
  const n = Number(s || 0);
  if (!n) return "0s";
  const m = Math.floor(n / 60), sec = n % 60;
  return m ? `${m}m ${sec}s` : `${sec}s`;
}

const STATUS_ICON = {
  answered: PhoneCall,
  completed: PhoneCall,
  no_answer: PhoneMissed,
  missed: PhoneMissed,
  failed: PhoneOff,
  busy: PhoneOff,
  cancelled: PhoneOff,
};

export function LeadCallHistoryDialog({ lead, open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManual, setShowManual] = useState(false);

  const load = async () => {
    if (!lead?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${lead.id}/call-history?limit=50`);
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load call history")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, lead?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none" data-testid="lead-call-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-4 h-4" /> Call log · {lead?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Actions */}
          <div className="flex gap-2 border p-2 bg-neutral-50">
            <div className="flex-1 text-xs">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Phone</div>
              <div className="font-mono">{lead?.phone || "—"}</div>
            </div>
            {lead?.phone && (
              <CallButton
                testid="lead-call-now"
                entityType="lead" entityId={lead.id} phone={lead.phone}
                label="Call now" small
                onCallEnd={load}
              />
            )}
            <Button size="sm" variant="outline" className="rounded-none" onClick={() => setShowManual((v) => !v)} data-testid="lead-manual-call-toggle">
              <PlusCircle className="w-3.5 h-3.5 mr-1" /> Manual
            </Button>
          </div>

          {showManual && (
            <ManualCallForm leadId={lead.id} onDone={() => { setShowManual(false); load(); }} />
          )}

          {/* History */}
          {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
           : items.length === 0 ? <div className="text-xs text-muted-foreground border p-4 text-center">No calls logged yet for this lead.</div> : (
            <div className="max-h-[45vh] overflow-y-auto border" data-testid="lead-call-history-list">
              {items.map((c) => {
                const Icon = STATUS_ICON[c.status] || Phone;
                return (
                  <div key={c.id} className="p-2 text-xs border-b last:border-b-0 flex items-start gap-2" data-testid={`call-log-row-${c.id}`}>
                    <Icon className={`w-4 h-4 mt-0.5 ${["answered", "completed"].includes(c.status) ? "text-emerald-600" : "text-rose-600"}`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold uppercase">{c.direction || "outbound"}</span>
                        <span>· {c.status || "unknown"}</span>
                        <span>· {fmtDur(c.duration_sec)}</span>
                        <span className="ml-auto text-muted-foreground text-[10px]">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                      {c.agent_name && <div className="text-muted-foreground">Agent: {c.agent_name}</div>}
                      {c.recording_url && <a href={c.recording_url} target="_blank" rel="noreferrer" className="text-sky-600 underline">Recording</a>}
                      {c.notes && <div className="mt-0.5 whitespace-pre-wrap">{c.notes}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManualCallForm({ leadId, onDone }) {
  const [direction, setDirection] = useState("outbound");
  const [status, setStatus] = useState("completed");
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      // Log via lead follow-up API — creates a followup entry and shows up in history.
      await api.post(`/leads/${leadId}/followups`, {
        channel: "call",
        note: `Manual call log — direction=${direction}, status=${status}, duration=${duration || 0}s${notes ? `\n${notes}` : ""}`,
        outcome: status,
      });
      toast.success("Manual call logged");
      setDuration(""); setNotes("");
      onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Log failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="border p-2 space-y-1.5" data-testid="lead-manual-call-form">
      <div className="grid grid-cols-3 gap-1.5">
        <Select value={direction} onValueChange={setDirection}>
          <SelectTrigger className="h-8 rounded-none text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="outbound">Outbound</SelectItem>
            <SelectItem value="inbound">Inbound</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 rounded-none text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="no_answer">No answer</SelectItem>
            <SelectItem value="busy">Busy</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3 text-muted-foreground" />
          <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="sec" className="h-8 rounded-none text-xs" />
        </div>
      </div>
      <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Call notes…" className="rounded-none text-xs" />
      <div className="flex justify-end">
        <Button size="sm" className="rounded-none h-8" onClick={submit} disabled={busy} data-testid="lead-manual-call-submit">Log call</Button>
      </div>
    </div>
  );
}

export function TemperaturePill({ value, onChange, testid }) {
  const label = value || "—";
  const cls = {
    hot:  "bg-rose-100 text-rose-700 border-rose-300",
    warm: "bg-amber-100 text-amber-800 border-amber-300",
    cold: "bg-sky-100 text-sky-700 border-sky-300",
  }[value] || "bg-neutral-100 text-neutral-500 border-neutral-300";
  if (!onChange) {
    return <span className={`text-[10px] px-1.5 py-0.5 border uppercase ${cls}`} data-testid={testid}>{label}</span>;
  }
  return (
    <Select value={value || "__clear__"} onValueChange={(v) => onChange(v === "__clear__" ? "" : v)}>
      <SelectTrigger className={`h-6 rounded-none text-[10px] px-1 uppercase w-20 ${cls}`} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__clear__">(auto)</SelectItem>
        <SelectItem value="hot">Hot</SelectItem>
        <SelectItem value="warm">Warm</SelectItem>
        <SelectItem value="cold">Cold</SelectItem>
      </SelectContent>
    </Select>
  );
}
